<template>
    <div class="about-page">
        <section class="about-hero" data-aos="fade-up">
            <span class="eyebrow-tag"> درباره ما </span>
            <h1 class="hero-title">
                لایوی، نمایش <span class="gradient-text"> زنده </span> جهان
            </h1>
            <p class="hero-desc">
                در دنیایی پر از خبر، گم شدن میان داده‌ها آسان است. لایوی با
                تمرکز بر مکان واقعی هر اتفاق، به شما کمک می‌کند تا فقط در یک
                نگاه ببینید کجا چه چیزی در حال رخ دادن است. هدف ما ساده است:
                دسترسی منصفانه، دقیق و لحظه‌ای به اطلاعات برای همه.
            </p>
        </section>

        <section class="about-section" data-aos="fade-up" data-aos-delay="120">
            <div class="section-head">
                <span class="eyebrow-tag"> ارزش‌های ما </span>
                <h2 class="section-title"> سه اصلی که به آن پایبندیم </h2>
            </div>
            <div class="values-grid">
                <article class="value-card" v-for="(v, i) in values" :key="v.title"
                         :data-aos="'fade-up'" :data-aos-delay="160 + i * 80">
                    <div class="value-icon" :class="v.iconClass" v-html="v.icon"></div>
                    <h3 class="value-title">{{ v.title }}</h3>
                    <p class="value-desc">{{ v.desc }}</p>
                </article>
            </div>
        </section>

        <section class="about-section" data-aos="fade-up" data-aos-delay="160">
            <div class="section-head">
                <span class="eyebrow-tag"> ویژگی‌ها </span>
                <h2 class="section-title"> چه چیزی لایوی را متمایز می‌کند؟ </h2>
            </div>
            <div class="features-grid">
                <div class="feature-row" v-for="(f, i) in features" :key="f.title"
                     :data-aos="'fade-up'" :data-aos-delay="200 + i * 60">
                    <div class="feature-bullet">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                        </svg>
                    </div>
                    <div>
                        <h4 class="feature-title">{{ f.title }}</h4>
                        <p class="feature-desc">{{ f.desc }}</p>
                    </div>
                </div>
            </div>
        </section>

        <section class="about-cta" data-aos="fade-up" data-aos-delay="200">
            <h2 class="cta-title">
                آماده‌اید جهان را روی نقشه ببینید؟
            </h2>
            <p class="cta-desc">
                همین حالا نقشه‌ی زنده‌ی لایوی را باز کنید و تجربه‌ای متفاوت از
                دنبال کردن خبرها داشته باشید.
            </p>
            <a href="/map" class="cta-btn">
                مشاهده نقشه
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M19 12H5M12 5l-7 7 7 7" />
                </svg>
            </a>
        </section>
    </div>
</template>

<script>
    import AOS from "aos";
    import { buildMeta } from "@/utils/seo";

    export default {
        name: "AboutUs",
        metaInfo() {
            return buildMeta({
                title: "درباره لایوی",
                description: "با لایوی، پلتفرم نمایش زنده اخبار و رویدادهای جهان روی نقشه، و مأموریت ما برای رساندن سریع‌ترین اخبار آشنا شوید.",
                path: "/aboutus",
                type: "website",
            });
        },
        data() {
            return {
                values: [
                    {
                        title: "سرعت",
                        desc: "خبرها در همان لحظه‌ای که اتفاق می‌افتند روی نقشه نمایش داده می‌شوند.",
                        iconClass: "icon-cyan",
                        icon: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                            </svg>`
                    },
                    {
                        title: "دقت",
                        desc: "هر رویداد دقیقاً روی نقطه‌ای از نقشه قرار می‌گیرد که در دنیای واقعی رخ داده.",
                        iconClass: "icon-blue",
                        icon: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <circle cx="12" cy="12" r="3" />
                            </svg>`
                    },
                    {
                        title: "شفافیت",
                        desc: "منبع هر خبر مشخص است و کاربر می‌تواند مستقیماً به مرجع اصلی مراجعه کند.",
                        iconClass: "icon-purple",
                        icon: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                <polyline points="9 12 11 14 15 10" />
                            </svg>`
                    }
                ],
                features: [
                    {
                        title: "پوشش زنده‌ی جهانی",
                        desc: "از خاورمیانه تا آمریکای جنوبی، رویدادها به محض وقوع روی نقشه ظاهر می‌شوند."
                    },
                    {
                        title: "موقعیت‌یابی دقیق",
                        desc: "نقطه‌گذاری هر خبر بر پایه‌ی مختصات واقعی، نه شهر یا کشور کلی."
                    },
                    {
                        title: "گروه‌بندی هوشمند",
                        desc: "وقتی چند خبر در یک نقطه باشند، لایوی آن‌ها را به‌شکل تمیز در یک خوشه نشان می‌دهد."
                    },
                    {
                        title: "تایم‌لاین تاریخی",
                        desc: "نه فقط امروز — می‌توانید رویدادهای گذشته را هم روی نقشه و در زمان مرور کنید."
                    }
                ]
            };
        },
        created() {
            AOS.init({ once: true, duration: 700, easing: "ease-out-cubic" });
        }
    }
</script>

<style lang="scss" scoped>
    @import "~aos/dist/aos.css";
    @import "@/assets/css/tokens";

    .about-page {
        max-width: 980px;
        margin: 0 auto;
        padding: 6rem 1.5rem 5rem;
    }

    .about-hero {
        text-align: center;
        margin-bottom: 4rem;
    }

    .hero-title {
        font-size: clamp(2rem, 4.5vw, 3rem);
        font-weight: 900;
        line-height: 1.4;
        margin: 0.6rem 0 1.1rem;
    }

    .hero-desc {
        max-width: 640px;
        margin: 0 auto;
        font-size: 1rem;
        color: $text-secondary;
        line-height: 2.1;
    }

    .about-section {
        margin-bottom: 4rem;
    }

    .section-head {
        text-align: center;
        margin-bottom: 2.2rem;
    }

    .section-title {
        font-size: clamp(1.4rem, 3vw, 1.9rem);
        font-weight: 800;
        margin: 0.4rem 0 0;
    }

    .mission-card {
        @include section-card;
        padding: 2.5rem 2.5rem;
        text-align: center;
        position: relative;
        overflow: hidden;

        &::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 4px;
            background: $gradient-main;
        }
    }

    .section-body {
        font-size: 0.97rem;
        color: $text-secondary;
        line-height: 2.1;
        margin: 0.8rem auto 0;
        max-width: 620px;
        text-align: justify;
    }

    .values-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 1.2rem;
    }

    .value-card {
        @include section-card;
        padding: 1.8rem 1.4rem;
        text-align: center;

        &:hover {
            transform: translateY(-4px);
            border-color: rgba($accent-blue, 0.18);
            box-shadow: 0 12px 30px rgba($accent-blue, 0.06);
        }
    }

    .value-icon {
        width: 54px;
        height: 54px;
        border-radius: 16px;
        margin: 0 auto 1rem;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .icon-cyan {
        background: rgba($accent-cyan, 0.1);
        color: $accent-cyan;
    }

    .icon-blue {
        background: rgba($accent-blue, 0.1);
        color: $accent-blue;
    }

    .icon-purple {
        background: rgba($accent-purple, 0.1);
        color: $accent-purple;
    }

    .value-title {
        font-size: 1.05rem;
        font-weight: 800;
        margin: 0 0 0.5rem;
        color: $text-primary;
    }

    .value-desc {
        font-size: 0.85rem;
        color: $text-secondary;
        line-height: 1.95;
        margin: 0;
    }

    .features-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 1.4rem 2rem;
        max-width: 820px;
        margin: 0 auto;
    }

    .feature-row {
        display: flex;
        gap: 0.9rem;
        align-items: flex-start;
    }

    .feature-bullet {
        flex-shrink: 0;
        width: 28px;
        height: 28px;
        border-radius: 8px;
        background: rgba($accent-teal, 0.12);
        color: $accent-teal;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 2px;
    }

    .feature-title {
        font-size: 0.95rem;
        font-weight: 700;
        margin: 0 0 0.3rem;
        color: $text-primary;
    }

    .feature-desc {
        font-size: 0.84rem;
        color: $text-secondary;
        line-height: 1.95;
        margin: 0;
    }

    .about-cta {
        text-align: center;
        background: $gradient-soft;
        border: 1px solid rgba($accent-cyan, 0.15);
        border-radius: 22px;
        padding: 2.6rem 1.6rem;
        margin-top: 1.5rem;
    }

    .cta-title {
        font-size: clamp(1.4rem, 3vw, 1.9rem);
        font-weight: 900;
        margin: 0 0 0.8rem;
    }

    .cta-desc {
        font-size: 0.95rem;
        color: $text-secondary;
        margin: 0 auto 1.4rem;
        max-width: 520px;
        line-height: 1.95;
    }

    .cta-btn {
        @include pill-button;
    }

    @media (max-width: 768px) {
        .about-page {
            padding: 5rem 1.1rem 3.5rem;
        }
        .values-grid {
            grid-template-columns: 1fr;
        }
        .features-grid {
            grid-template-columns: 1fr;
        }
        .mission-card {
            padding: 1.8rem 1.3rem;
        }
    }
</style>
